import { blogPosts as initialCoreBlogPosts, BLOG_DEFAULT_IMAGE } from "./blogPosts";
import { supabase, isSupabaseConfigured } from "../utils/supabaseClient";

const CUSTOM_POSTS_STORAGE_KEY = "clubhosty_custom_posts_v2";
const DRAFTS_STORAGE_KEY = "clubhosty_blog_drafts_v2";
const DELETED_POSTS_STORAGE_KEY = "clubhosty_deleted_posts_v2";
const CHANGE_EVENT_NAME = "clubhosty_blogs_updated";

// Helper to safely access localStorage
const safeStorage = {
  getItem: (key) => {
    try {
      return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, value);
      }
    } catch (e) {
      console.warn("Storage write failed:", e);
    }
  },
  removeItem: (key) => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn("Storage remove failed:", e);
    }
  },
};

// Parse JSON safely
const parseJson = (str, fallback = []) => {
  if (!str) return fallback;
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

// Format slug to kebab-case
export const formatSlug = (text = "") => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Estimate read time from word count
export const calculateReadTime = (content) => {
  if (!content) return "3 min read";
  let totalWords = 0;

  if (typeof content === "string") {
    totalWords = content.trim().split(/\s+/).length;
  } else if (Array.isArray(content)) {
    content.forEach((sec) => {
      if (sec.heading) totalWords += sec.heading.split(/\s+/).length;
      if (Array.isArray(sec.paragraphs)) {
        sec.paragraphs.forEach((p) => {
          totalWords += p.split(/\s+/).length;
        });
      }
      if (Array.isArray(sec.list)) {
        sec.list.forEach((li) => {
          totalWords += li.split(/\s+/).length;
        });
      }
      if (sec.rawContent) {
        totalWords += sec.rawContent.split(/\s+/).length;
      }
    });
  }

  const minutes = Math.max(1, Math.ceil(totalWords / 200));
  return `${minutes} min read`;
};

// Dispatch change event to keep UI sync across tabs/components
export const notifyBlogChange = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT_NAME));
  }
};

// Helper to migrate legacy placeholder to modern local images
export const OLD_PLACEHOLDER_IMAGE =
  "https://res.cloudinary.com/clubhosty/image/upload/v1774777350/ChatGPT_Image_Mar_29_2026_03_12_17_PM_o0pufi.png";

export const resolvePostImage = (customImage, coreImage) => {
  if (customImage && customImage !== OLD_PLACEHOLDER_IMAGE) {
    return customImage;
  }
  if (coreImage && coreImage !== OLD_PLACEHOLDER_IMAGE) {
    return coreImage;
  }
  return BLOG_DEFAULT_IMAGE;
};

/**
 * Convert frontend Post object to Supabase database row
 */
export const mapPostToSupabase = (post) => {
  return {
    slug: post.slug,
    title: post.title,
    category: post.category || "Hosting",
    date:
      post.date ||
      new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    read_time: post.readTime || "5 min read",
    image: resolvePostImage(post.image),
    excerpt: post.excerpt || "",
    meta_title: post.metaTitle || post.title || "",
    meta_description: post.metaDescription || post.excerpt || "",
    keywords: Array.isArray(post.keywords) ? post.keywords : [],
    clubhosty_links: Array.isArray(post.clubhostyLinks) ? post.clubhostyLinks : [],
    faqs: Array.isArray(post.faqs) ? post.faqs : [],
    related_posts: Array.isArray(post.relatedPosts) ? post.relatedPosts : [],
    content: Array.isArray(post.content) ? post.content : [],
    raw_content: post.rawContent || "",
    is_draft: Boolean(post.isDraft),
    updated_at: new Date().toISOString(),
  };
};

/**
 * Convert Supabase database row to frontend Post object
 */
export const mapSupabaseToPost = (row) => {
  const core = initialCoreBlogPosts.find((p) => p.slug === row.slug);
  return {
    slug: row.slug,
    title: row.title,
    category: row.category || "Hosting",
    date: row.date,
    readTime: row.read_time || "5 min read",
    image: resolvePostImage(row.image, core?.image),
    excerpt: row.excerpt || "",
    metaTitle: row.meta_title || row.title,
    metaDescription: row.meta_description || row.excerpt || "",
    keywords: Array.isArray(row.keywords) ? row.keywords : [],
    clubhostyLinks: Array.isArray(row.clubhosty_links) ? row.clubhosty_links : [],
    faqs: Array.isArray(row.faqs) ? row.faqs : [],
    relatedPosts: Array.isArray(row.related_posts) ? row.related_posts : [],
    content: Array.isArray(row.content) ? row.content : [],
    rawContent: row.raw_content || "",
    isDraft: Boolean(row.is_draft),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    isCustom: true,
    isSupabase: true,
  };
};

/**
 * Check if the Supabase blog_posts table is configured and ready
 */
export const checkSupabaseTableStatus = async () => {
  if (!isSupabaseConfigured() || !supabase) {
    return {
      configured: false,
      tableExists: false,
      message: "Supabase credentials not configured in .env",
    };
  }

  try {
    const { error } = await supabase.from("blog_posts").select("slug").limit(1);

    if (error) {
      const isMissing =
        error.code === "PGRST205" ||
        (error.message && error.message.toLowerCase().includes("could not find the table"));

      return {
        configured: true,
        tableExists: false,
        isTableMissing: isMissing,
        code: error.code,
        message: error.message,
      };
    }

    return {
      configured: true,
      tableExists: true,
      message: "Connected to Supabase (blog_posts ready)",
    };
  } catch (err) {
    return {
      configured: true,
      tableExists: false,
      message: err.message,
    };
  }
};

/**
 * Fetch all posts and drafts from Supabase, cache into localStorage, and update UI
 */
export const fetchPostsFromSupabase = async () => {
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      // Table may not exist yet or connection failed
      console.warn("Supabase blog fetch note:", error.message);
      return { success: false, error: error.message };
    }

    if (Array.isArray(data)) {
      const remotePublished = [];
      const remoteDrafts = [];

      data.forEach((row) => {
        const post = mapSupabaseToPost(row);
        if (post.isDraft) {
          remoteDrafts.push(post);
        } else {
          remotePublished.push(post);
        }
      });

      // Merge remote posts into local custom posts storage
      const localCustom = getCustomPosts();
      const customMap = new Map();
      localCustom.forEach((p) => {
        if (p && p.slug) customMap.set(p.slug, p);
      });
      // Remote Supabase posts take priority for cross-device sync
      remotePublished.forEach((p) => {
        customMap.set(p.slug, p);
      });

      safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(Array.from(customMap.values())));

      // Merge drafts
      if (remoteDrafts.length > 0) {
        const localDrafts = getDraftPosts();
        const draftsMap = new Map();
        localDrafts.forEach((d) => {
          if (d && d.slug) draftsMap.set(d.slug, d);
        });
        remoteDrafts.forEach((d) => {
          draftsMap.set(d.slug, d);
        });
        safeStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(Array.from(draftsMap.values())));
      }

      // Automatically save any local or core posts that are not yet in Supabase database
      const remoteSlugs = new Set(data.map((r) => r.slug));
      const allLocal = getAllBlogPosts({ includeDrafts: true });
      const missingInDb = allLocal.filter((p) => p && p.slug && !remoteSlugs.has(p.slug));

      if (missingInDb.length > 0) {
        const payloads = missingInDb.map(mapPostToSupabase);
        supabase
          .from("blog_posts")
          .upsert(payloads, { onConflict: "slug" })
          .then(({ error: autoSaveErr }) => {
            if (autoSaveErr) {
              console.warn("Auto-save to Supabase notice:", autoSaveErr.message);
            }
          })
          .catch((err) => console.warn("Auto-save to Supabase error:", err));
      }

      notifyBlogChange();
      return { success: true, count: data.length, posts: remotePublished, drafts: remoteDrafts };
    }
  } catch (err) {
    console.warn("Supabase fetch failed:", err);
    return { success: false, error: err.message };
  }

  return null;
};

/**
 * Fetch a single post by slug directly from Supabase (for direct links or uncached posts)
 */
export const fetchBlogPostBySlug = async (slug) => {
  if (!slug || !supabase) return null;

  try {
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();

    if (data && !error) {
      const post = mapSupabaseToPost(data);
      // Cache in local storage if not a draft
      if (!post.isDraft) {
        const custom = getCustomPosts();
        const existingIdx = custom.findIndex((c) => c.slug === post.slug);
        if (existingIdx >= 0) {
          custom[existingIdx] = post;
        } else {
          custom.unshift(post);
        }
        safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(custom));
        notifyBlogChange();
      }
      return post;
    }
  } catch (err) {
    console.warn("Error fetching post from Supabase by slug:", err);
  }

  return null;
};

/**
 * Get all custom/overridden posts from localStorage
 */
export const getCustomPosts = () => {
  return parseJson(safeStorage.getItem(CUSTOM_POSTS_STORAGE_KEY), []);
};

/**
 * Get all draft posts from localStorage
 */
export const getDraftPosts = () => {
  return parseJson(safeStorage.getItem(DRAFTS_STORAGE_KEY), []);
};

/**
 * Get slugs of deleted core posts
 */
export const getDeletedSlugs = () => {
  return parseJson(safeStorage.getItem(DELETED_POSTS_STORAGE_KEY), []);
};

/**
 * Check if a slug belongs to a core (initial) post
 */
export const isCorePost = (slug) => {
  return initialCoreBlogPosts.some((p) => p.slug === slug);
};

/**
 * Returns a list of all blog posts merged:
 * 1. Default core posts (42+ items)
 * 2. Overridden/edited versions of core posts
 * 3. Newly created custom posts (from local storage and synced Supabase)
 * 4. Filters out deleted posts
 * 5. Optionally includes drafts
 */
export const getAllBlogPosts = ({ includeDrafts = false } = {}) => {
  const customPosts = getCustomPosts();
  const deletedSlugs = getDeletedSlugs();
  const drafts = includeDrafts ? getDraftPosts() : [];

  // Map of custom overrides by slug
  const customMap = new Map();
  customPosts.forEach((post) => {
    if (post && post.slug) {
      customMap.set(post.slug, post);
    }
  });

  // Base list starting with initial core posts
  const resultList = [];
  const processedSlugs = new Set();

  initialCoreBlogPosts.forEach((corePost) => {
    if (deletedSlugs.includes(corePost.slug)) return; // Skipped if deleted

    processedSlugs.add(corePost.slug);

    if (customMap.has(corePost.slug)) {
      // Use edited version
      const customPost = customMap.get(corePost.slug);
      resultList.push({
        ...corePost,
        ...customPost,
        image: resolvePostImage(customPost?.image, corePost.image),
        isCore: true,
        isEdited: true,
      });
    } else {
      // Use original core post
      resultList.push({
        ...corePost,
        image: resolvePostImage(null, corePost.image),
        isCore: true,
        isEdited: false,
      });
    }
  });

  // Add new custom posts that are not overrides of core posts
  customPosts.forEach((post) => {
    if (!processedSlugs.has(post.slug) && !deletedSlugs.includes(post.slug)) {
      processedSlugs.add(post.slug);
      resultList.push({
        ...post,
        image: resolvePostImage(post.image),
        isCore: false,
        isCustom: true,
      });
    }
  });

  // Add drafts if requested
  if (includeDrafts) {
    drafts.forEach((draft) => {
      resultList.push({
        ...draft,
        image: resolvePostImage(draft.image),
        isDraft: true,
      });
    });
  }

  // Sort by date (newest first)
  return resultList.sort((a, b) => {
    const timeA = new Date(a.date).getTime() || 0;
    const timeB = new Date(b.date).getTime() || 0;
    return timeB - timeA;
  });
};

/**
 * Fetch a single post by slug (checks custom/edited, core, and drafts)
 */
export const getBlogPostBySlug = (slug) => {
  if (!slug) return null;

  const deletedSlugs = getDeletedSlugs();
  if (deletedSlugs.includes(slug)) return null;

  // 1. Check custom posts / overrides
  const customPosts = getCustomPosts();
  const customMatch = customPosts.find((p) => p.slug === slug);

  // 2. Check core posts
  const coreMatch = initialCoreBlogPosts.find((p) => p.slug === slug);

  if (customMatch && coreMatch) {
    return {
      ...coreMatch,
      ...customMatch,
      image: resolvePostImage(customMatch.image, coreMatch.image),
      isCore: true,
      isEdited: true,
    };
  }

  if (customMatch) {
    return {
      ...customMatch,
      image: resolvePostImage(customMatch.image),
      isCore: false,
      isCustom: true,
    };
  }

  if (coreMatch) {
    return {
      ...coreMatch,
      image: resolvePostImage(null, coreMatch.image),
      isCore: true,
      isEdited: false,
    };
  }

  // 3. Fallback check drafts
  const drafts = getDraftPosts();
  const draftMatch = drafts.find((d) => d.slug === slug);
  if (draftMatch) {
    return {
      ...draftMatch,
      image: resolvePostImage(draftMatch.image),
      isDraft: true,
    };
  }

  return null;
};

/**
 * Save or publish a blog post (Saves to Supabase Database AND Local Cache)
 */
export const saveBlogPost = async (postData, { isDraft = false } = {}) => {
  if (!postData || !postData.title) {
    throw new Error("Post title is required.");
  }

  const slug = formatSlug(postData.slug || postData.title);
  if (!slug) {
    throw new Error("Valid slug is required.");
  }

  const cleanPost = {
    ...postData,
    slug,
    isDraft,
    readTime: postData.readTime || calculateReadTime(postData.content || postData.rawContent),
    date:
      postData.date ||
      new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    image: postData.image?.trim() || BLOG_DEFAULT_IMAGE,
    updatedAt: new Date().toISOString(),
  };

  // 1. Always save immediately to local storage for fast offline resilience
  if (isDraft) {
    const drafts = getDraftPosts();
    const index = drafts.findIndex((d) => d.slug === slug);
    if (index >= 0) {
      drafts[index] = cleanPost;
    } else {
      drafts.unshift(cleanPost);
    }
    safeStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
  } else {
    // If it was previously in drafts, remove from drafts
    const drafts = getDraftPosts().filter((d) => d.slug !== slug);
    safeStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));

    // Save into custom posts
    const customPosts = getCustomPosts();
    const index = customPosts.findIndex((p) => p.slug === slug);
    if (index >= 0) {
      customPosts[index] = cleanPost;
    } else {
      customPosts.unshift(cleanPost);
    }
    safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(customPosts));

    // Unmark from deleted if it was previously marked deleted
    const deletedSlugs = getDeletedSlugs().filter((s) => s !== slug);
    safeStorage.setItem(DELETED_POSTS_STORAGE_KEY, JSON.stringify(deletedSlugs));
  }

  notifyBlogChange();

  // 2. Persist to Supabase Database
  if (supabase) {
    try {
      const payload = mapPostToSupabase(cleanPost);
      const { error } = await supabase
        .from("blog_posts")
        .upsert(payload, { onConflict: "slug" });

      if (error) {
        console.warn("Supabase save warning:", error.message);
        cleanPost._supabase = { synced: false, error: error.message };
      } else {
        cleanPost._supabase = { synced: true };
      }
    } catch (err) {
      console.warn("Supabase network error:", err);
      cleanPost._supabase = { synced: false, error: err.message };
    }
  } else {
    cleanPost._supabase = { synced: false, error: "Supabase client not configured" };
  }

  return cleanPost;
};

/**
 * Delete a post (removes from both local cache and Supabase)
 */
export const deleteBlogPost = async (slug) => {
  if (!slug) return;

  // 1. Remove from local storage
  const customPosts = getCustomPosts().filter((p) => p.slug !== slug);
  safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(customPosts));

  const drafts = getDraftPosts().filter((d) => d.slug !== slug);
  safeStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));

  if (isCorePost(slug)) {
    const deletedSlugs = getDeletedSlugs();
    if (!deletedSlugs.includes(slug)) {
      deletedSlugs.push(slug);
      safeStorage.setItem(DELETED_POSTS_STORAGE_KEY, JSON.stringify(deletedSlugs));
    }
  }

  notifyBlogChange();

  // 2. Remove from Supabase
  if (supabase) {
    try {
      const { error } = await supabase.from("blog_posts").delete().eq("slug", slug);
      if (error) {
        console.warn("Supabase delete warning:", error.message);
      }
    } catch (err) {
      console.warn("Supabase delete failed:", err);
    }
  }
};

/**
 * Reset an edited core post back to its default code definition
 */
export const restoreCorePost = async (slug) => {
  if (!slug) return;

  const customPosts = getCustomPosts().filter((p) => p.slug !== slug);
  safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(customPosts));

  const deletedSlugs = getDeletedSlugs().filter((s) => s !== slug);
  safeStorage.setItem(DELETED_POSTS_STORAGE_KEY, JSON.stringify(deletedSlugs));

  notifyBlogChange();

  if (supabase) {
    try {
      await supabase.from("blog_posts").delete().eq("slug", slug);
    } catch (err) {
      console.warn("Supabase restoreCorePost delete error:", err);
    }
  }
};

/**
 * Sync / Push all current posts (core + custom) into Supabase
 */
export const syncAllToSupabase = async () => {
  if (!supabase) throw new Error("Supabase is not configured.");

  const all = getAllBlogPosts({ includeDrafts: true });
  const payloads = all.map(mapPostToSupabase);

  const { error } = await supabase
    .from("blog_posts")
    .upsert(payloads, { onConflict: "slug" });

  if (error) throw error;
  return { success: true, count: payloads.length };
};

/**
 * Returns latest blog post for header / banner
 */
export const getLatestBlogPost = () => {
  const all = getAllBlogPosts({ includeDrafts: false });
  return all.length > 0 ? all[0] : null;
};

/**
 * Subscribe to blog data changes
 */
export const subscribeToBlogChanges = (callback) => {
  if (typeof window === "undefined") return () => {};

  const handler = () => callback();
  window.addEventListener(CHANGE_EVENT_NAME, handler);
  window.addEventListener("storage", handler);

  return () => {
    window.removeEventListener(CHANGE_EVENT_NAME, handler);
    window.removeEventListener("storage", handler);
  };
};

/**
 * Export all posts data as JSON string for backup/sharing
 */
export const exportAllPostsAsJson = () => {
  const allPosts = getAllBlogPosts({ includeDrafts: true });
  return JSON.stringify(allPosts, null, 2);
};

/**
 * Import posts from JSON string
 */
export const importPostsFromJson = async (jsonString) => {
  try {
    const posts = JSON.parse(jsonString);
    if (!Array.isArray(posts)) {
      throw new Error("Invalid format: expected an array of blog posts.");
    }

    const currentCustom = getCustomPosts();
    const map = new Map();
    currentCustom.forEach((p) => map.set(p.slug, p));

    const validPosts = [];
    posts.forEach((p) => {
      if (p && p.slug && p.title) {
        map.set(p.slug, p);
        validPosts.push(p);
      }
    });

    const merged = Array.from(map.values());
    safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(merged));
    notifyBlogChange();

    // Optionally sync imported to Supabase
    if (supabase && validPosts.length > 0) {
      try {
        const payloads = validPosts.map(mapPostToSupabase);
        await supabase.from("blog_posts").upsert(payloads, { onConflict: "slug" });
      } catch (e) {
        console.warn("Could not sync imported posts to Supabase:", e);
      }
    }

    return { success: true, count: posts.length };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

/**
 * Generate formatted ES6 JS code for a blog post
 * Matches exact structure of src/Blog/posts/*.js
 */
export const generateJsPostModule = (post) => {
  const varName =
    post.slug
      .replace(/-([a-z0-9])/g, (g) => g[1].toUpperCase())
      .replace(/^[0-9]/, "_$&") + "Post";

  return `const ${varName} = ${JSON.stringify(post, null, 2)};\n\nexport default ${varName};\n`;
};

// Automatic initial fetch from Supabase when running in browser
if (typeof window !== "undefined") {
  // Delay slightly to allow initial render without blocking
  setTimeout(() => {
    fetchPostsFromSupabase().catch(() => {});
  }, 300);
}
